import React, { forwardRef, useCallback, useMemo, useState } from "react";
import { FieldValues, useController } from "react-hook-form";
import { Input, InputRef } from "antd5";
import classNames from "classnames";

import Button from "components/ui/button/Button";
import { createUseDebounce } from "lib/debounce";
import { RELEVANCY_SEARCH_NEW_USERS, useVariableValue } from "lib/featureFlags";
import { useSignalSettings } from "lib/hooks/api/teams/useSignalSettings";
import { red500 } from "lib/themes/colors";
import { ALL_KEYWORDS_TOKEN } from "lib/types/models";
import { completeUnclosedQuotes, simpleArrayDedupe } from "lib/utils";
import { searchRefraction, slashCircle1 } from "../../../lib/icons/untitled_ui/SVGs";
import UIcon from "../../../lib/icons/untitled_ui/UIcon";
import { SelectProps } from "../Inputs";
import SaveSignalsButton from "../SaveSignalsButton";
import KeywordDropdownMenu from "./KeywordDropdownMenu";
import SelectedKeywordTags from "./SelectedKeywordTags";

import css from "./KeywordSelect.module.scss";

type FieldProps<T extends FieldValues> = Omit<SelectProps<T>, "options"> & {
  containerClassName?: string;
  type?: "keywords" | "excludeKeywords";
};

function KeywordSelectInner<T extends FieldValues>(
  { type = "keywords", ...props }: FieldProps<T>,
  ref: React.Ref<InputRef>,
) {
  const isRelevancySearchEnabled = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);
  const [searchText, setSearchText] = useState<string>("");

  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);

  const useDebounce = createUseDebounce(500);

  const delayDropdownOpen = useDebounce(setDropdownOpen);

  const { field } = useController(props);

  const { value, onChange } = field;

  const { data: signals, isLoading } = useSignalSettings();

  const allKeywords = useMemo((): string[] => {
    if (type === "excludeKeywords") {
      return [];
    }

    return signals?.keywords || [];
  }, [signals?.keywords, type]);

  /**
   * A bit of an extension onto the normal onChange function, this will add or remove the ALL_KEYWORDS_TOKEN
   * from the filter value, depending on whether or not all keywords are selected
   */
  const removeAllKeywordsToken = (keywords: string[]) =>
    keywords.filter((t: string) => t !== ALL_KEYWORDS_TOKEN);

  const onKeywordsChange = useCallback(
    (newKeywords: string[]) => {
      const containsAllKeywords =
        allKeywords.length > 0 && allKeywords.every((s) => newKeywords.includes(s));

      if (containsAllKeywords) {
        newKeywords.push(ALL_KEYWORDS_TOKEN);
      } else {
        newKeywords = removeAllKeywordsToken(newKeywords);
      }

      onChange(newKeywords);
    },
    [allKeywords, onChange],
  );

  const selectedKeywords = useMemo((): string[] => {
    if (!value || !Array.isArray(value)) {
      return [];
    }

    // We can safely assume that the value is an array of strings
    const valueArray = value as string[];

    // Just in case, we will make absolutely sure that IF this token is present in the array,
    // keyword signals are added for sure and the token is removed
    if (valueArray.includes(ALL_KEYWORDS_TOKEN)) {
      return simpleArrayDedupe([...valueArray, ...allKeywords]).filter(
        (t: string) => t !== ALL_KEYWORDS_TOKEN,
      );
    }
    return value;
  }, [value, allKeywords]);

  const { containerClassName, label } = props;

  // Replace with a basic check as we're disabling signals icon
  // while we test with users
  // TODO: cleanup once we decide to keep or remove
  /* const showIconForTag = useCallback(
    (keyword: string): boolean => {
      // Always show icon for exclude keyword
      if (type === "excludeKeywords") {
        return true;
      }

      // Only show icon when keyword is saved as a signal
      // return allKeywords.includes(keyword);
      return false
    },
    [allKeywords, type],
  );*/

  const freeTextKeywords = useMemo(
    () => selectedKeywords.filter((k) => !allKeywords.includes(k)),
    [allKeywords, selectedKeywords],
  );

  return (
    <div
      className={classNames(css.container, containerClassName)}
      onBlur={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) {
          return;
        }
        setDropdownOpen(false);
      }}
    >
      {label && (
        <div className={css.filterLabelDiv} aria-label="filterLabel">
          <span className={css.filterName}>{label}</span>
        </div>
      )}
      <Input
        aria-label="keywordSearchInput"
        className={css.inputField}
        onPressEnter={(e) => {
          e.preventDefault();
          if (!searchText.length) return;
          // Complete any unclosed quotes in the searchText
          const cleanedSearchText = completeUnclosedQuotes(searchText);
          if (selectedKeywords.includes(cleanedSearchText)) {
            onKeywordsChange(selectedKeywords.filter((v: string) => v !== cleanedSearchText));
          } else {
            onKeywordsChange([...selectedKeywords, cleanedSearchText]);
          }

          setSearchText("");
          delayDropdownOpen(false);
        }}
        onClick={() => setDropdownOpen(!dropdownOpen)}
        onChange={(e) => {
          const searchText = e.currentTarget.value;

          // if we're typing, open the dropdown
          if (searchText.length > 0 && !dropdownOpen) {
            setDropdownOpen(true);
          }

          setSearchText(searchText);
        }}
        placeholder={type === "keywords" ? "Search keywords" : "Type and press enter"}
        prefix={<UIcon svg={searchRefraction} size={16} />}
        value={searchText}
        ref={ref}
      />
      <KeywordDropdownMenu
        allKeywords={removeAllKeywordsToken(allKeywords)}
        dropdownOpen={dropdownOpen}
        onChange={onKeywordsChange}
        selectedKeywords={removeAllKeywordsToken(selectedKeywords)}
        searchText={searchText}
        type={type}
      />
      {selectedKeywords && selectedKeywords.length > 0 && !dropdownOpen && (
        <>
          <div className={css.filterLabelDiv} aria-label="clearDiv">
            <span className={css.filterName}>{`${selectedKeywords.length} selected`}</span>
            <Button
              buttonType="secondary"
              className={css.clearButton}
              onClick={() => {
                onChange([]);
                delayDropdownOpen(false);
              }}
            >
              Clear
            </Button>
          </div>

          <SelectedKeywordTags
            selectedKeywords={selectedKeywords}
            tooltipTitle={type === "keywords" ? "Keyword signal" : undefined}
            isLoading={isLoading}
            onChange={onChange}
            closeDropdown={() => delayDropdownOpen(false)}
            icon={
              type === "excludeKeywords" && (
                <UIcon className={css.icon} svg={slashCircle1} size={16} color={red500} />
              ) // hide for now while we test with users
              // TODO: cleanup once we decide to keep or remove
              // <Signal className={css.icon} size={16} label="signalIcon" />
            }
            // showIconForTag={showIconForTag}
            showIcon={type === "excludeKeywords" ? true : false}
            tagClassname={type === "excludeKeywords" ? css.excludeTag : undefined}
          />
          {type === "keywords" && freeTextKeywords.length > 0 && !isRelevancySearchEnabled && (
            <SaveSignalsButton
              addedSignalValues={freeTextKeywords}
              signalType="keyword"
              existingSignalValues={allKeywords}
            />
          )}
        </>
      )}
    </div>
  );
}

const KeywordSelect = forwardRef(KeywordSelectInner) as <T extends FieldValues>(
  props: FieldProps<T> & { ref?: React.Ref<InputRef> },
) => ReturnType<typeof KeywordSelectInner>;

export { KeywordSelect };
